import { Box, Grid } from "@mui/material";
import CrossButton from "assets/Primary_white.svg";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import NoPlansBanner from "components/common/NoPlansBanner";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Timing from "components/common/Timing";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { uniq, uniqueId } from "lodash";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import {
  abovePropertyViewTypePlan,
  abovePropertyViewTypeTemplate,
  accessTypes,
  tabConstants
} from "utils/CommonConstants";
import { getHoursAndMin, getSourceName } from "utils/CommonFunctions";
import { pageNames } from "utils/Types";
import { routes } from "utils/routes";
import { updateBookmarkedPlan } from "./AboveProperty.slice";

const BookmarkedPlans = (props: any) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { isMobileScreen } = useContext(ThemeContext);
  const { bookmarkedIdList, bookmarkedTemplate } = useAppSelector((state) => state.aboveProperty);

  const removeBookmarked = async (cmsId: any, viewType: string) => {
    if (viewType === abovePropertyViewTypePlan)
      await dispatch(
        updateBookmarkedPlan({
          viewType: viewType,
          payload: {
            bookmarkPlans: bookmarkedIdList?.filter((item: any) => item.cmsId !== cmsId)
          }
        })
      );
    else
      await dispatch(
        updateBookmarkedPlan({
          viewType: viewType,
          payload: {
            bookmarkTemplatePlans: bookmarkedTemplate?.filter((item: any) => item.templatePlanId !== cmsId)
          }
        })
      );

    dispatch(
      setFeedbackDetails({
        shouldShow: true,
        variant: "success",
        setState: setFeedbackDetails({ shouldShow: false }),
        message: "Plan removed from bookmarked."
      })
    );
  };

  const getBookmarkedListView = (bookmarkedList: any, viewType: string) => {
    return bookmarkedList?.map((element: any) => {
      return (
        <Grid
          key={uniqueId("PLAN_")}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "15px",
            borderRadius: "12px",
            cursor: "pointer",
            padding: "16px",
            paddingBottom: "25px",
            boxShadow:
              "0 0 6px rgba(0, 0, 0, 0.05), 0 4px 6px rgba(0, 0, 0, 0.05), 4px 0 6px rgba(0, 0, 0, 0.05), -4px 0 6px rgba(0, 0, 0, 0.05)" // Lighter shadow color
          }}
          onClick={() =>
            navigate(
              `${routes.PlanProgress}/${
                viewType === "Template" ? element?.templatePlanId : element?.cmsId
              }?redirectFrom=${
                element?.bookmarkSource === "template_plans"
                  ? tabConstants.AssignedCustomPlans
                  : tabConstants.libraryPlans
              }`,
              viewType === "Plan"
                ? {
                    state: { watchListPlan: true }
                  }
                : {}
            )
          }
        >
          <Grid container>
            <Grid item md={11}>
              <Timing duration={getHoursAndMin(element?.timeEstimateMinutes)} />
            </Grid>
            <Grid marginLeft={"auto"} item md={1}>
              <Button
                sx={{ paddingTop: "0" }}
                iconButton
                icon={CrossButton}
                height="30"
                accessType={accessTypes.update}
                onClick={(e) => {
                  e.stopPropagation();
                  removeBookmarked(
                    viewType === translate("dashboard.AboveProperty.Bookmarked.Template")
                      ? element?.templatePlanId
                      : element?.cmsId,
                    viewType === translate("dashboard.AboveProperty.Bookmarked.Template")
                      ? abovePropertyViewTypeTemplate
                      : abovePropertyViewTypePlan
                  );
                }}
              ></Button>
            </Grid>
          </Grid>

          <Grid>
            <Typography variant="body2" showBold color={tokens.Text.General.PrimaryHeadline}>
              {element?.planName}
            </Typography>
          </Grid>
          <Box>
            <Typography
              sx={{ display: "flex", flexDirection: "row", gap: "4px" }}
              variant={"label"}
              color={tokens.Text.General.Tertiary}
            >
              Source:
              <Box component="span" sx={{ color: tokens.Text.General.Tertiary, fontWeight: 600 }}>
                {bookmarkedList
                  ?.filter((item: any) =>
                    viewType === translate("dashboard.AboveProperty.Bookmarked.Template")
                      ? item?.id === element?.id
                      : item?.cmsId === element?.cmsId
                  )
                  .map((item2: any) => {
                    return (
                      <Box
                        key={uniqueId("SOURCE_")}
                        display={"flex"}
                        flexDirection={"row"}
                        gap={"10px"}
                        flexWrap={"wrap"}
                      >
                        {uniq(item2?.planSource?.map((source: any) => getSourceName(source))).join(", ")}
                      </Box>
                    );
                  })}
              </Box>
            </Typography>
          </Box>
        </Grid>
      );
    });
  };

  return (
    <Grid>
      <Grid
        id="actionItemsDiv"
        item
        xs={12}
        sx={{
          maxHeight: "65vh",
          overflowY: "auto"
        }}
      >
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "24px"
            }}
          >
            <Typography variant="h5" showBold color={tokens.Text.General.PrimaryHeadline}>
              {translate("common.bookmarkedPlans")}
            </Typography>
          </Box>

          {bookmarkedIdList?.length > 0 || bookmarkedTemplate?.length > 0 ? (
            <Grid
              paddingX={"3px"}
              paddingBottom={"30px"}
              maxHeight={isMobileScreen ? "55vh" : "75vh"}
              display={"flex"}
              flexDirection={"column"}
              gap="30px"
            >
              {getBookmarkedListView(bookmarkedIdList, "Plan")}
              {getBookmarkedListView(bookmarkedTemplate, "Template")}
            </Grid>
          ) : (
            <NoPlansBanner pageName={pageNames.abovePropertyPlans} type={pageNames.abovePropertyPlans} isDashboard />
          )}
        </>
      </Grid>
    </Grid>
  );
};

export default BookmarkedPlans;
